import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityType,
  Box,
  CategoryIcon,
  costFormater,
  HiddenLine,
  InfoOutlineIcon,
  Inscription,
  RawButton,
  Responsive,
  Text,
  useResponsive,
  WarningCard,
} from '@common-fe/common-fe';
import dayjs from 'dayjs';
import { capitalize } from 'lodash';

import { REGEXPS } from '@/common';
import { DEFAULT_CARD_EXP_DATE_FORMAT } from '@/common/constants';
import {
  PaymentType,
  Service,
  TransactionServiceForData,
  TransactionType,
} from '@/modules/transaction/transaction.types';
import getYearsOldTitle from '@/utils/modifiers/getYearsOldTitle';

import { useGetTransactionTypeLabel } from './hooks/useGetTransactionTypeLabel';
import ProviderModalWrapper from './ProviderModal/ProviderModalWrapper';
import ProviderView from './ProviderModal/ProviderView';
import {
  // AmountsWrapper,
  ColumnText,
  ColumnTitle,
  // FooterItem,
  // FooterLabel,
  // FooterText,
  IdBlock,
  Title,
  TransactionTypeLabel,
  // Wrapper,
} from './TransactionCard.styles';

const MAX_ONE_LINE_PROVIDER_NAME_LENGTH = 30;
const MAX_WIDTH_OF_PROVIDER_NAME = '460px';

interface TransactionCardFooterItemProps {
  label: string;
  value: string;
}
const TransactionCardFooterItem: React.FC<TransactionCardFooterItemProps> = ({ label, value }) => {
  const { isMobile } = useResponsive();
  return (
    <Box
      direction="row"
      justify={isMobile ? 'between' : 'start'}
      style={{ flex: 1 }}
      width={{ min: '225px' }}
    >
      <Text color="textSecondary" size="large">
        {label}
      </Text>
      <Text size="large" weight={700} margin={{ left: 'spacing4' }}>
        {value}
      </Text>
    </Box>
  );
};
interface Props {
  className?: string;
  invoiceId?: string;
  id?: number | string;
  category: ActivityType;
  providerName: string;
  amount: number;
  approvedAmount?: number;
  paidAmount?: number;
  userName?: string;
  userRelationship?: string;
  birthOn?: string;
  service?: Service;
  transactionType: TransactionType | PaymentType;
  date?: Date | string;
  claimId?: string;
  transactorId?: string | number;
  applyDeductibleAmount?: number | null;
  serviceFor?: TransactionServiceForData;
  taxYear?: string;
  employeeNotes?: string;
}

const TransactionCard: React.FC<Props> = ({
  className,
  invoiceId,
  id,
  category,
  providerName,
  amount,
  approvedAmount,
  paidAmount,
  userName,
  service,
  transactionType,
  date,
  claimId,
  transactorId,
  userRelationship,
  birthOn,
  applyDeductibleAmount,
  serviceFor,
  taxYear,
  employeeNotes,
}) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const [openAddModal, setOpenModal] = useState<boolean>(false);

  const { transactionTypeLabel, transactionDateLabel, color, background } = useGetTransactionTypeLabel(
    transactionType,
    !claimId
  );

  const isLongProviderName = useMemo(
    () => providerName.split(' ')[0].length > MAX_ONE_LINE_PROVIDER_NAME_LENGTH,
    [providerName]
  );
  const birthOnTitle = useMemo(() => {
    if (!birthOn) {
      return '';
    }

    return `, ${getYearsOldTitle(birthOn)}`;
  }, [birthOn]);
  const description = useMemo(() => {
    if (!userRelationship) return '';

    return `(${userRelationship}${birthOnTitle})`;
  }, [userRelationship, birthOnTitle]);

  const isAdjustmentsType = useMemo(() => {
    if (
      category === ActivityType.ADJUSTMENTS ||
      category === ActivityType.ADJUSTMENT ||
      category === ActivityType.CLAIM_BALANCE_ADJUSTMENT
    ) {
      return true;
    }
    return false;
  }, [category]);
  return (
    <Box
      width="100%"
      round="container1Round"
      background="canvas"
      className={className}
      data-testid="TransactionCard-wrapper"
      pad={isMobile ? 'spacing16' : { horizontal: 'spacing32', top: 'spacing32', bottom: 'spacing24' }}
      border={{ size: 'small', color: 'border2' }}
    >
      <Box
        direction={isMobile ? 'column' : 'row'}
        align={isMobile ? 'start' : 'center'}
        justify="between"
        border={{ side: 'bottom', size: 'small', color: 'border1' }}
        pad={isMobile ? {} : { bottom: 'spacing24' }}
      >
        <Box fill direction="row">
          <Box alignSelf="center" width={{ min: '48px' }}>
            <CategoryIcon category={category} size="l" />
          </Box>
          <Box
            direction="column"
            fill={isMobile}
            justify="center"
            width={{ max: MAX_WIDTH_OF_PROVIDER_NAME }}
          >
            <Box width="fit-content">
              <Title
                isHovered={!!transactorId}
                title={providerName}
                isLong={isLongProviderName}
                onClick={transactorId ? () => setOpenModal(true) : () => null}
              >
                {providerName}
              </Title>
            </Box>

            {transactionType !== PaymentType.CARD_SWIPE && (
              <Box direction="row" margin={{ left: 'spacing16' }}>
                <HiddenLine
                  data={userName}
                  isAvailable={serviceFor?.isViewNameAllowed}
                  isHidden={serviceFor?.isHidden}
                  customTitleColor="textBody"
                  description={description}
                />
              </Box>
            )}
          </Box>
          <Responsive size={Responsive.Mobile}>
            {!isAdjustmentsType && !!transactorId && (
              <RawButton disabled={!transactorId} onClick={() => setOpenModal(true)}>
                <InfoOutlineIcon size="24px" color="iconSecondary" />
              </RawButton>
            )}
          </Responsive>
        </Box>
        <Box
          direction={isMobile ? 'row' : 'column'}
          align={isMobile ? 'center' : 'end'}
          justify={isMobile ? 'between' : 'start'}
          margin={isMobile ? { top: 'spacing12' } : {}}
          pad={isMobile ? { bottom: 'spacing12' } : {}}
        >
          <TransactionTypeLabel className="card-button" onClick={() => {}} style={{ color, background }}>
            {transactionTypeLabel}
          </TransactionTypeLabel>

          {!!id && <IdBlock>{`#${id}`}</IdBlock>}
        </Box>
      </Box>
      <Box
        direction={isMobile ? 'column' : 'row'}
        border={isMobile ? undefined : { size: 'small', side: 'bottom', color: 'border1' }}
        justify="between"
        pad={isMobile ? {} : { vertical: 'spacing32' }}
      >
        <Box
          direction={isMobile ? 'row' : 'column'}
          justify={isMobile ? 'between' : undefined}
          align={isMobile ? 'center' : undefined}
          margin={isMobile ? { top: 'spacing16' } : {}}
        >
          <ColumnTitle>{claimId ? t('Paid Amount') : t('Amount')}</ColumnTitle>
          <Text size="3xl" weight={700} color="textAccent" margin={isMobile ? {} : { top: 'spacing16' }}>
            {costFormater(claimId ? paidAmount : amount, true)}
          </Text>
        </Box>
        {amount >= 0 && claimId && (
          <Box
            gap={isMobile ? 'spacing12' : 'spacing16'}
            pad={
              isMobile
                ? {
                    top: 'spacing12',
                  }
                : { bottom: 'spacing24' }
            }
          >
            <Box
              direction="row"
              justify={isMobile ? 'between' : undefined}
              alignSelf={isMobile ? undefined : 'end'}
            >
              <ColumnTitle margin={{ right: 'spacing4' }}>
                {transactionType !== PaymentType.CARD_SWIPE
                  ? t('Requested amount: ')
                  : t('Card swiped amount: ')}
              </ColumnTitle>
              <ColumnText>{`$${amount.toFixed(2)}`}</ColumnText>
            </Box>
            <Box
              direction="row"
              alignSelf={isMobile ? undefined : 'end'}
              justify={isMobile ? 'between' : undefined}
            >
              <ColumnTitle margin={{ right: 'spacing4' }}>Approved amount:</ColumnTitle>
              <ColumnText>{costFormater(approvedAmount, true)}</ColumnText>
            </Box>
            {!!applyDeductibleAmount && (
              <Box direction="row">
                <ColumnTitle margin={{ right: 'spacing4' }}>Applied to deductible:</ColumnTitle>
                <ColumnText isInfoColor>{`$${applyDeductibleAmount?.toFixed(2)}`}</ColumnText>
              </Box>
            )}
          </Box>
        )}
      </Box>
      <Box
        direction={isMobile ? 'column' : 'row'}
        gap={isMobile ? 'spacing12' : 'spacing8'}
        pad={isMobile ? { top: 'spacing12' } : { top: 'spacing24' }}
        style={{ flexWrap: 'wrap' }}
      >
        <TransactionCardFooterItem
          label={transactionDateLabel}
          value={date ? dayjs(date).format(DEFAULT_CARD_EXP_DATE_FORMAT) : ''}
        />

        {service && (
          <TransactionCardFooterItem
            label={t('Service provided:')}
            value={capitalize(service.categoryType.replace(REGEXPS.DASH_SYMBOL, ''))}
          />
        )}
        {invoiceId && <TransactionCardFooterItem label={t('Invoice:')} value={invoiceId} />}
        {taxYear && <TransactionCardFooterItem label={t('Tax year:')} value={taxYear} />}
      </Box>

      {employeeNotes && (
        <Box margin={{ top: 'spacing16' }}>
          <WarningCard
            testId="employee-notes"
            title={<Inscription weight={700}>{t('Notes')}</Inscription>}
            containerProps={{ pad: 'spacing16', round: 'container1Round' }}
            topBorderColor="transparent"
            backgroundColor="module"
            borderColor="border1"
          >
            <Box margin={{ top: 'spacing8' }}>
              <Inscription>{employeeNotes}</Inscription>
            </Box>
          </WarningCard>
        </Box>
      )}

      <ProviderModalWrapper
        visible={openAddModal}
        onSetVisible={() => setOpenModal(false)}
        modalTitle="Provider information"
      >
        <ProviderView
          transactorId={`${transactorId || ''}`}
          onCancel={() => {
            setOpenModal(false);
          }}
        />
      </ProviderModalWrapper>
    </Box>
  );
};
export default TransactionCard;
